import { useMemo } from 'react';
import type { FormOrPacketToSend, MinimalPersonData } from '@frontend/form-packet-selector';
import { ModalControlResponse } from '@frontend/design-system';
import { FormLinkGeneratedData, SendFormsMode, SendFormsOrigin, SendFormsStepsConfig } from '../../types';
import useDocumentToSendConfig from './useDocumentToSendConfig';
import usePersonConfig from './usePersonConfig';
import useStepsConfig from './useStepsConfig';

export interface CommonProps {
  sendFormsModalControls: ModalControlResponse;
  locationIds: string[];
  mode?: SendFormsMode;
  steps?: SendFormsStepsConfig;
  onComplete?: (data: FormLinkGeneratedData) => void;
}

export interface FromFormsContextProps {
  origin: SendFormsOrigin.FORMS;
  document: FormOrPacketToSend;
}

export interface FromContactsContextProps {
  origin: SendFormsOrigin.CONTACTS;
  person: MinimalPersonData;
}

export interface FromCompleteContextProps {
  origin: SendFormsOrigin.COMPLETE;
}
export interface FromInvalidContextProps {
  origin: SendFormsOrigin.NONE;
}

export interface FromKioskContextProps {
  origin: SendFormsOrigin.KIOSK;
}

export interface FromMessagingContextProps {
  origin: SendFormsOrigin.MESSAGING;
  personId: string;
  mode?: SendFormsMode;
  steps?: SendFormsStepsConfig;
  onComplete?: (data: FormLinkGeneratedData) => void;
}

export type SpecialProps =
  | FromFormsContextProps
  | FromContactsContextProps
  | FromCompleteContextProps
  | FromKioskContextProps
  | FromMessagingContextProps
  | FromInvalidContextProps;
export type UseSendFormsProvidersProps = CommonProps & SpecialProps;
export type UseSendFormsProvidersResults = UseSendFormsProvidersProps;

export const useSendFormsProviders = (props: UseSendFormsProvidersProps): UseSendFormsProvidersResults => {
  const { locationIds, origin, sendFormsModalControls, mode, steps, onComplete } = props;
  let document: FormOrPacketToSend | null = null;
  let person: MinimalPersonData | null = null;
  let personId: string | null = null;

  if (origin === SendFormsOrigin.FORMS) {
    document = props.document;
  }

  if (origin === SendFormsOrigin.CONTACTS) {
    person = props.person;
  }

  if (origin === SendFormsOrigin.MESSAGING) {
    personId = props.personId;
  }

  useStepsConfig({ origin, locationIds });
  useDocumentToSendConfig({ documentToSend: document });
  usePersonConfig({ person, personId });

  const results = useMemo<UseSendFormsProvidersResults>(() => {
    if (origin === SendFormsOrigin.FORMS) {
      if (!document) {
        throw new Error('Document is required for SendFormsOrigin.FORMS');
      }

      return {
        locationIds,
        origin,
        sendFormsModalControls,
        document,
        mode,
        steps,
        onComplete,
      };
    }

    if (origin === SendFormsOrigin.CONTACTS) {
      if (!person) {
        throw new Error('Person is required for SendFormsOrigin.CONTACTS');
      }

      return {
        locationIds,
        origin,
        sendFormsModalControls,
        person,
        mode,
        steps,
        onComplete,
      };
    }

    if (origin === SendFormsOrigin.MESSAGING) {
      if (!personId) {
        throw new Error('PersonId is required for SendFormsOrigin.MESSAGING');
      }

      return {
        locationIds,
        origin,
        sendFormsModalControls,
        personId,
        mode,
        steps,
        onComplete,
      };
    }

    return {
      locationIds,
      origin,
      sendFormsModalControls,
      mode,
      steps,
      onComplete,
    };
  }, [locationIds, origin, sendFormsModalControls, document, person, personId, mode, steps, onComplete]);

  return results;
};
